import React, { useState, useEffect, useMemo } from 'react';
import charityData from '../../constants/data-charity';
import ShareButton from '../common/ShareButton';

const CharityCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mouseStart, setMouseStart] = useState(null);
  const [mouseEnd, setMouseEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cardsPerPage, setCardsPerPage] = useState(1); // Default to 1 for mobile
  const [lastSwipeTime, setLastSwipeTime] = useState(0);
  const [isSwipeDisabled, setIsSwipeDisabled] = useState(false);

  // Filter and sort charity data
  const filteredCharityData = useMemo(() => {
    return charityData.filter((item) => item.enable === true).sort((a, b) => a.top - b.top);
  }, []);

  // Determine cards per page based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // lg breakpoint
        setCardsPerPage(3);
      } else if (window.innerWidth >= 768) {
        // md breakpoint
        setCardsPerPage(2);
      } else {
        // sm and below
        setCardsPerPage(1);
      }
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto play functionality
  useEffect(() => {
    if (!isAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        if (nextIndex >= filteredCharityData.length) {
          return 0; // Reset to beginning
        }
        return nextIndex;
      });
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlay, filteredCharityData.length]);

  // Touch/Swipe functionality
  const minSwipeDistance = 100; // Increased from 50 to prevent accidental swipes
  const swipeDebounceTime = 300; // 300ms debounce between swipes

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || isSwipeDisabled) return;

    const currentTime = Date.now();
    if (currentTime - lastSwipeTime < swipeDebounceTime) {
      return; // Prevent rapid swipes
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      setLastSwipeTime(currentTime);
      setIsSwipeDisabled(true);

      // Temporarily disable swipe to prevent rapid triggering
      setTimeout(() => {
        setIsSwipeDisabled(false);
      }, swipeDebounceTime);

      if (isLeftSwipe) {
        // Swipe left - go to next slide
        setCurrentIndex((prevIndex) => {
          const nextIndex = prevIndex + 1;
          if (nextIndex >= filteredCharityData.length) {
            return 0; // Reset to beginning
          }
          return nextIndex;
        });
      } else if (isRightSwipe) {
        // Swipe right - go to previous slide
        setCurrentIndex((prevIndex) => {
          const prevIndexValue = prevIndex - 1;
          if (prevIndexValue < 0) {
            return filteredCharityData.length - 1; // Go to last
          }
          return prevIndexValue;
        });
      }

      // Pause auto-play and resume after 5 seconds
      setIsAutoPlay(false);
      setTimeout(() => {
        setIsAutoPlay(true);
      }, 5000);
    }
  };

  // Mouse drag functionality
  const onMouseDown = (e) => {
    setIsDragging(true);
    setMouseEnd(null);
    setMouseStart(e.clientX);
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    setMouseEnd(e.clientX);
  };

  const onMouseUp = () => {
    if (!isDragging || !mouseStart || !mouseEnd || isSwipeDisabled) {
      setIsDragging(false);
      return;
    }

    const currentTime = Date.now();
    if (currentTime - lastSwipeTime < swipeDebounceTime) {
      setIsDragging(false);
      return; // Prevent rapid drags
    }

    const distance = mouseStart - mouseEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      setLastSwipeTime(currentTime);
      setIsSwipeDisabled(true);

      // Temporarily disable swipe to prevent rapid triggering
      setTimeout(() => {
        setIsSwipeDisabled(false);
      }, swipeDebounceTime);

      if (isLeftSwipe) {
        // Drag left - go to next slide
        setCurrentIndex((prevIndex) => {
          const nextIndex = prevIndex + 1;
          if (nextIndex >= filteredCharityData.length) {
            return 0; // Reset to beginning
          }
          return nextIndex;
        });
      } else if (isRightSwipe) {
        // Drag right - go to previous slide
        setCurrentIndex((prevIndex) => {
          const prevIndexValue = prevIndex - 1;
          if (prevIndexValue < 0) {
            return filteredCharityData.length - 1; // Go to last
          }
          return prevIndexValue;
        });
      }

      // Pause auto-play and resume after 5 seconds
      setIsAutoPlay(false);
      setTimeout(() => {
        setIsAutoPlay(true);
      }, 5000);
    }

    setIsDragging(false);
  };

  const onMouseLeave = () => {
    setIsDragging(false);
  };

  const formatAmount = (amount) => {
    if (!amount) return '0 VND';
    return new Intl.NumberFormat('vi-VN').format(amount) + ' VND';
  };

  const formatCount = (count) => {
    return new Intl.NumberFormat('vi-VN').format(count);
  };

  const handlePopupOpen = () => {
    setIsAutoPlay(false);
  };

  const handlePopupClose = () => {
    setIsAutoPlay(true);
  };

  return (
    <div className="w-full mx-auto px-2">
      {/* Carousel Container */}
      <div
        className="overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl cursor-grab active:cursor-grabbing"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        <div
          className={`flex transition-transform duration-500 ease-in-out ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{
            transform: `translateX(-${currentIndex * (100 / cardsPerPage)}%)`,
            transition: isDragging ? 'none' : 'transform 500ms ease-in-out',
          }}
        >
          {/* Duplicate cards for infinite loop */}
          {[...filteredCharityData, ...filteredCharityData, ...filteredCharityData].map(
            (charity, index) => (
              <div
                key={`charity-${charity.top}-${index}`}
                className={`flex-shrink-0 px-1 ${
                  cardsPerPage === 3 ? 'w-1/3' : cardsPerPage === 2 ? 'w-1/2' : 'w-full'
                }`}
              >
                <div className="bg-white rounded-md sm:rounded-lg md:rounded-xl shadow-sm sm:shadow-md md:shadow-lg overflow-hidden h-full flex flex-col border-2 border-[#f9ab6b8a]">
                  {/* Image */}
                  <div className="relative h-[18rem]">
                    <img
                      src={charity.avatarLink}
                      alt={charity.title}
                      className="w-full h-full object-cover"
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Content */}
                  <div className="p-1.5 sm:p-2 md:p-3 lg:p-4 flex flex-col flex-1">
                    {/* Title */}
                    <h3 className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-bold text-[#fe6601] mb-1 sm:mb-1.5 md:mb-2 line-clamp-2 leading-tight">
                      {charity.title}
                    </h3>

                    {/* Description */}
                    <div className="flex-1 mb-1.5 sm:mb-2 md:mb-3">
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line line-clamp-4">
                        {charity.description}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="flex flex-col gap-1 sm:gap-1.5 md:gap-2 mb-1.5 sm:mb-2 md:mb-3">
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-600 font-bold">Quỹ chia sẻ</div>
                        <div className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-[#fe6601] leading-tight">
                          {formatAmount(charity.countShare * 50000 || 0)}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1.5 sm:mb-2 md:mb-3">
                          <div className="text-xs text-gray-600 font-bold">Lượt chia sẻ</div>
                          <div className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-[#fe6601] leading-tight">
                            {formatCount(charity.countShare)} lượt
                          </div>
                        </div>
                        <ShareButton
                          charity={charity}
                          className="w-full py-1 sm:py-1.5 md:py-2 px-2 sm:px-3 md:px-4 rounded-md sm:rounded-lg text-xs sm:text-sm mt-auto"
                          onPopupOpen={handlePopupOpen}
                          onPopupClose={handlePopupClose}
                        >
                          Chia sẻ
                        </ShareButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default CharityCarousel;
